import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Paper,
  Table,
  Button,
  TextInput,
  Group,
  Badge,
  ActionIcon,
  Modal,
  Stack,
  Alert,
  Loader,
  Pagination,
  Select,
  Box,
  Grid,
  Divider,
  Tabs,
  NumberInput,
  Checkbox
} from '@mantine/core';
import { 
  IconSearch, 
  IconEdit, 
  IconTrash, 
  IconEye, 
  IconPlus,
  IconCalculator,
  IconReport,
  IconSettings,
  IconReceipt,
  IconCreditCard,
  IconPercentage
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { API_URL } from '../../../utils/constants';

function TaxesScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || 'list';
  
  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTax, setSelectedTax] = useState(null);
  const [editingTax, setEditingTax] = useState({
    tax_code: '',
    description: '',
    tax1: '',
    tax2: '',
    tax3: '',
    tax4: '',
    tax5: '',
    tax6: '',
    sort_order: 0
  });

  // Check authentication on component mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
  }, [navigate]);

  useEffect(() => {
    fetchTaxes();
  }, [searchTerm]);

  useEffect(() => {
    // Handle mode-based actions
    if (mode === 'add') {
      handleAdd();
    } else if (mode === 'change' && selectedTax) {
      handleEdit(selectedTax);
    } else if (mode === 'delete' && selectedTax) {
      handleDelete(selectedTax);
    }
  }, [mode, selectedTax]);

  const fetchTaxes = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const params = new URLSearchParams();
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      const response = await fetch(`${API_URL}/taxes?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTaxes(data);
      } else {
        if (response.status === 401) {
          navigate('/login');
          return;
        }
        const errorData = await response.json();
        setError(errorData.message || 'Error fetching taxes');
      }
    } catch (error) {
      console.error('Error fetching taxes:', error);
      setError('Error fetching taxes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingTax({
      account_code: '',
      transaction_code: '',
      description: '',
      tax1: '',
      tax2: '',
      tax3: '',
      tax4: '',
      tax5: '',
      tax6: ''
    });
    setShowAddModal(true);
  };

  const handleEdit = (tax) => {
    setEditingTax({
      id: tax.id,
      tax_code: tax.tax_code || '',
      description: tax.description || '',
      tax1: tax.tax1 || '',
      tax2: tax.tax2 || '',
      tax3: tax.tax3 || '',
      tax4: tax.tax4 || '',
      tax5: tax.tax5 || '',
      tax6: tax.tax6 || '',
      sort_order: tax.sort_order || 0
    });
    setShowEditModal(true);
  };

  const handleDelete = (tax) => {
    setSelectedTax(tax);
    setShowDeleteModal(true);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTaxes();
  };

  const handleRowClick = (tax) => {
    setSelectedTax(tax);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_URL}/taxes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editingTax),
      });

      if (response.ok) {
        const newTax = await response.json();
        setSuccess('Tax created successfully!');
        setShowAddModal(false);
        window.history.replaceState(null, '', '/dashboard/taxes?mode=list');
        fetchTaxes();
      } else {
        if (response.status === 401) {
          navigate('/login');
          return;
        }
        const errorData = await response.json();
        setError(errorData.message || 'Error creating tax');
      }
    } catch (error) {
      console.error('Error creating tax:', error);
      setError('Error creating tax. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_URL}/taxes/${editingTax.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editingTax),
      });

      if (response.ok) {
        setSuccess('Tax updated successfully!');
        setShowEditModal(false);
        window.history.replaceState(null, '', '/dashboard/taxes?mode=list');
        fetchTaxes();
      } else {
        if (response.status === 401) {
          navigate('/login');
          return;
        }
        const errorData = await response.json();
        setError(errorData.message || 'Error updating tax');
      }
    } catch (error) {
      console.error('Error updating tax:', error);
      setError('Error updating tax. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_URL}/taxes/${selectedTax.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setSuccess('Tax deleted successfully!');
        setShowDeleteModal(false);
        window.history.replaceState(null, '', '/dashboard/taxes?mode=list');
        fetchTaxes();
      } else {
        if (response.status === 401) {
          navigate('/login');
          return;
        }
        const errorData = await response.json();
        setError(errorData.message || 'Error deleting tax');
      }
    } catch (error) {
      console.error('Error deleting tax:', error);
      setError('Error deleting tax. Please try again.');
    }
  };

  const handleMoveUp = async (tax) => {
    const currentIndex = taxes.indexOf(tax);
    if (currentIndex <= 0) return;

    const sortedTaxes = [...taxes].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    const currentTax = sortedTaxes[currentIndex];
    const previousTax = sortedTaxes[currentIndex - 1];

    // Swap sort orders
    const newCurrentSortOrder = previousTax.sort_order || 0;
    const newPreviousSortOrder = currentTax.sort_order || 0;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      // Update both taxes
      await Promise.all([
        fetch(`${API_URL}/taxes/${currentTax.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ ...currentTax, sort_order: newCurrentSortOrder }),
        }),
        fetch(`${API_URL}/taxes/${previousTax.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ ...previousTax, sort_order: newPreviousSortOrder }),
        })
      ]);

      // Refresh the list
      fetchTaxes();
      setSuccess('Tax order updated successfully!');
    } catch (error) {
      console.error('Error moving tax up:', error);
      setError('Error updating tax order. Please try again.');
    }
  };

  const handleMoveDown = async (tax) => {
    const currentIndex = taxes.indexOf(tax);
    if (currentIndex >= taxes.length - 1) return;

    const sortedTaxes = [...taxes].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    const currentTax = sortedTaxes[currentIndex];
    const nextTax = sortedTaxes[currentIndex + 1];

    // Swap sort orders
    const newCurrentSortOrder = nextTax.sort_order || 0;
    const newNextSortOrder = currentTax.sort_order || 0;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      // Update both taxes
      await Promise.all([
        fetch(`${API_URL}/taxes/${currentTax.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ ...currentTax, sort_order: newCurrentSortOrder }),
        }),
        fetch(`${API_URL}/taxes/${nextTax.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ ...nextTax, sort_order: newNextSortOrder }),
        })
      ]);

      // Refresh the list
      fetchTaxes();
      setSuccess('Tax order updated successfully!');
    } catch (error) {
      console.error('Error moving tax down:', error);
      setError('Error updating tax order. Please try again.');
    }
  };

  const handleInputChange = (field, value) => {
    setEditingTax(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <Container size="lg" py="xl">
        <Group justify="center">
          <Loader size="lg" />
        </Group>
      </Container>
    );
  }

  return (
    <Container fluid p={0} style={{ height: '100vh' }}>
      <Grid gutter={0} style={{ height: '100%' }}>
        {/* Side Menu */}
        <Grid.Col span={1.2} style={{ backgroundColor: 'var(--mantine-color-gray-0)', padding: '0.5rem', borderRight: '1px solid var(--mantine-color-gray-2)' }}>
          <Stack gap="xs">
            <Title order={6} c="dark" mb="xs" ta="center">Taxes</Title>
            
            {/* Primary Actions */}
            <Button 
              variant="subtle" 
              color="blue"
              fullWidth
              size="sm"
              onClick={() => window.history.replaceState(null, '', '/dashboard/taxes?mode=list')}
              leftSection={<IconSearch size="0.8rem" />}
            >
              Find
            </Button>
            <Button 
              variant="subtle" 
              color="green"
              fullWidth
              size="sm"
              onClick={() => {
                window.history.replaceState(null, '', '/dashboard/taxes?mode=add');
                handleAdd();
              }}
              leftSection={<IconPlus size="0.8rem" />}
            >
              Add
            </Button>
            <Button 
              variant="subtle" 
              color="orange"
              fullWidth
              size="sm"
              onClick={() => {
                if (selectedTax) {
                  window.history.replaceState(null, '', '/dashboard/taxes?mode=change');
                  handleEdit(selectedTax);
                }
              }}
              disabled={!selectedTax}
              leftSection={<IconEdit size="0.8rem" />}
            >
              Change
            </Button>
            <Button 
              variant="subtle" 
              color="red"
              fullWidth
              size="sm"
              onClick={() => {
                if (selectedTax) {
                  window.history.replaceState(null, '', '/dashboard/taxes?mode=delete');
                  handleDelete(selectedTax);
                }
              }}
              disabled={!selectedTax}
              leftSection={<IconTrash size="0.8rem" />}
            >
              Delete
            </Button>
            
            <Divider color="gray.2" my="sm" />
            
            {/* Tax Specific Actions */}
            <Button 
              variant="subtle" 
              color="violet"
              fullWidth
              size="sm"
              leftSection={<IconPercentage size="0.8rem" />}
            >
              Tax Calculator
            </Button>
            <Button 
              variant="subtle" 
              color="indigo"
              fullWidth
              size="sm"
              leftSection={<IconCalculator size="0.8rem" />}
            >
              Tax Reports
            </Button>
            
            <Divider color="gray.2" my="sm" />
            
            {/* Reports and Settings */}
            <Button 
              variant="subtle" 
              color="grape"
              fullWidth
              size="sm"
              leftSection={<IconReport size="0.8rem" />}
            >
              Reports
            </Button>
            <Button 
              variant="subtle" 
              color="dark"
              fullWidth
              size="sm"
              leftSection={<IconSettings size="0.8rem" />}
            >
              Settings
            </Button>
          </Stack>
        </Grid.Col>

        {/* Main Content */}
        <Grid.Col span={10.8} style={{ padding: '1rem' }}>
          <Stack gap="lg">
            <Group justify="space-between">
              <Title order={1}>Taxes Management</Title>
            </Group>

            {error && (
              <Alert color="red" title="Error">
                {error}
              </Alert>
            )}

            {success && (
              <Alert color="green" title="Success">
                {success}
              </Alert>
            )}

            {/* Search Bar */}
            <Paper p="md" withBorder>
              <Group>
                <TextInput
                  placeholder="Search taxes by account code, transaction code, or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  leftSection={<IconSearch size="1rem" />}
                  style={{ flex: 1 }}
                />
                <Button onClick={handleSearch} variant="filled">
                  Search
                </Button>
                <Button 
                  onClick={() => setSearchTerm('')} 
                  disabled={!searchTerm} 
                  variant="outline"
                >
                  Clear
                </Button>
              </Group>
            </Paper>

            {/* Taxes Table */}
            <Paper withBorder>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Tax Code</Table.Th>
                    <Table.Th>Description</Table.Th>
                    <Table.Th>Sort Order</Table.Th>
                    <Table.Th>Tax 1</Table.Th>
                    <Table.Th>Tax 2</Table.Th>
                    <Table.Th>Tax 3</Table.Th>
                    <Table.Th>Tax 4</Table.Th>
                    <Table.Th>Tax 5</Table.Th>
                    <Table.Th>Tax 6</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {taxes.length === 0 ? (
                    <Table.Tr>
                      <Table.Td colSpan={11} ta="center">
                        <Text c="dimmed">No taxes found. Click "Add" to create one.</Text>
                      </Table.Td>
                    </Table.Tr>
                  ) : (
                    taxes.map((tax) => (
                      <Table.Tr 
                        key={tax.id}
                        onClick={() => handleRowClick(tax)}
                        style={{ 
                          cursor: 'pointer',
                          backgroundColor: selectedTax && selectedTax.id === tax.id ? 'var(--mantine-color-yellow-0)' : '',
                          border: selectedTax && selectedTax.id === tax.id ? '2px solid var(--mantine-color-yellow-6)' : ''
                        }}
                      >
                        <Table.Td><Text fw={500}>{tax.tax_code}</Text></Table.Td>
                        <Table.Td>{tax.description || ''}</Table.Td>
                        <Table.Td>{tax.sort_order || 0}</Table.Td>
                        <Table.Td>{tax.tax1 !== null ? `${tax.tax1}%` : ''}</Table.Td>
                        <Table.Td>{tax.tax2 !== null ? `${tax.tax2}%` : ''}</Table.Td>
                        <Table.Td>{tax.tax3 !== null ? `${tax.tax3}%` : ''}</Table.Td>
                        <Table.Td>{tax.tax4 !== null ? `${tax.tax4}%` : ''}</Table.Td>
                        <Table.Td>{tax.tax5 !== null ? `${tax.tax5}%` : ''}</Table.Td>
                        <Table.Td>{tax.tax6 !== null ? `${tax.tax6}%` : ''}</Table.Td>
                        <Table.Td>
                          <Group gap="xs">
                            <ActionIcon
                              size="sm"
                              variant="subtle"
                              color="blue"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMoveUp(tax);
                              }}
                              disabled={taxes.indexOf(tax) === 0}
                            >
                              ↑
                            </ActionIcon>
                            <ActionIcon
                              size="sm"
                              variant="subtle"
                              color="blue"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMoveDown(tax);
                              }}
                              disabled={taxes.indexOf(tax) === taxes.length - 1}
                            >
                              ↓
                            </ActionIcon>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))
                  )}
                </Table.Tbody>
              </Table>
            </Paper>
          </Stack>
        </Grid.Col>
      </Grid>

      {/* Add Tax Modal */}
      <Modal opened={showAddModal} onClose={() => setShowAddModal(false)} title="Add New Tax" size="lg">
        <Stack gap="md">
          <Grid>
            <Grid.Col span={4}>
              <TextInput
                label="Tax Code *"
                value={editingTax.tax_code}
                onChange={(e) => handleInputChange('tax_code', e.target.value)}
                placeholder="Enter tax code"
                required
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <TextInput
                label="Description"
                value={editingTax.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Enter description"
                required
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <NumberInput
                label="Sort Order"
                value={editingTax.sort_order}
                onChange={(value) => handleInputChange('sort_order', value || 0)}
                placeholder="0"
                min={0}
              />
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={4}>
              <NumberInput
                label="Tax 1 (%)"
                value={editingTax.tax1}
                onChange={(value) => handleInputChange('tax1', value)}
                placeholder="0.00"
                min={0}
                decimalScale={2}
                rightSection={<Text size="sm" c="dimmed">%</Text>}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <NumberInput
                label="Tax 2 (%)"
                value={editingTax.tax2}
                onChange={(value) => handleInputChange('tax2', value)}
                placeholder="0.00"
                min={0}
                decimalScale={2}
                rightSection={<Text size="sm" c="dimmed">%</Text>}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <NumberInput
                label="Tax 3 (%)"
                value={editingTax.tax3}
                onChange={(value) => handleInputChange('tax3', value)}
                placeholder="0.00"
                min={0}
                decimalScale={2}
                rightSection={<Text size="sm" c="dimmed">%</Text>}
              />
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={4}>
              <NumberInput
                label="Tax 4 (%)"
                value={editingTax.tax4}
                onChange={(value) => handleInputChange('tax4', value)}
                placeholder="0.00"
                min={0}
                decimalScale={2}
                rightSection={<Text size="sm" c="dimmed">%</Text>}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <NumberInput
                label="Tax 5 (%)"
                value={editingTax.tax5}
                onChange={(value) => handleInputChange('tax5', value)}
                placeholder="0.00"
                min={0}
                decimalScale={2}
                rightSection={<Text size="sm" c="dimmed">%</Text>}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <NumberInput
                label="Tax 6 (%)"
                value={editingTax.tax6}
                onChange={(value) => handleInputChange('tax6', value)}
                placeholder="0.00"
                min={0}
                decimalScale={2}
                rightSection={<Text size="sm" c="dimmed">%</Text>}
              />
            </Grid.Col>
          </Grid>
        </Stack>

        <Group justify="flex-end" mt="xl">
          <Button variant="outline" onClick={() => {
            setShowAddModal(false);
            window.history.replaceState(null, '', '/dashboard/taxes?mode=list');
          }}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} loading={saving}>
            Save Tax
          </Button>
        </Group>
      </Modal>

      {/* Edit Tax Modal */}
      <Modal opened={showEditModal} onClose={() => setShowEditModal(false)} title="Edit Tax" size="lg">
        <Stack gap="md">
          <Grid>
            <Grid.Col span={4}>
              <TextInput
                label="Tax Code *"
                value={editingTax.tax_code}
                onChange={(e) => handleInputChange('tax_code', e.target.value)}
                placeholder="Enter tax code"
                required
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <TextInput
                label="Description"
                value={editingTax.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Enter description"
                required
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <NumberInput
                label="Sort Order"
                value={editingTax.sort_order}
                onChange={(value) => handleInputChange('sort_order', value || 0)}
                placeholder="0"
                min={0}
              />
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={4}>
              <NumberInput
                label="Tax 1 (%)"
                value={editingTax.tax1}
                onChange={(value) => handleInputChange('tax1', value)}
                placeholder="0.00"
                min={0}
                decimalScale={2}
                rightSection={<Text size="sm" c="dimmed">%</Text>}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <NumberInput
                label="Tax 2 (%)"
                value={editingTax.tax2}
                onChange={(value) => handleInputChange('tax2', value)}
                placeholder="0.00"
                min={0}
                decimalScale={2}
                rightSection={<Text size="sm" c="dimmed">%</Text>}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <NumberInput
                label="Tax 3 (%)"
                value={editingTax.tax3}
                onChange={(value) => handleInputChange('tax3', value)}
                placeholder="0.00"
                min={0}
                decimalScale={2}
                rightSection={<Text size="sm" c="dimmed">%</Text>}
              />
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={4}>
              <NumberInput
                label="Tax 4 (%)"
                value={editingTax.tax4}
                onChange={(value) => handleInputChange('tax4', value)}
                placeholder="0.00"
                min={0}
                decimalScale={2}
                rightSection={<Text size="sm" c="dimmed">%</Text>}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <NumberInput
                label="Tax 5 (%)"
                value={editingTax.tax5}
                onChange={(value) => handleInputChange('tax5', value)}
                placeholder="0.00"
                min={0}
                decimalScale={2}
                rightSection={<Text size="sm" c="dimmed">%</Text>}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <NumberInput
                label="Tax 6 (%)"
                value={editingTax.tax6}
                onChange={(value) => handleInputChange('tax6', value)}
                placeholder="0.00"
                min={0}
                decimalScale={2}
                rightSection={<Text size="sm" c="dimmed">%</Text>}
              />
            </Grid.Col>
          </Grid>
        </Stack>

        <Group justify="flex-end" mt="xl">
          <Button variant="outline" onClick={() => {
            setShowEditModal(false);
            window.history.replaceState(null, '', '/dashboard/taxes?mode=list');
          }}>
            Cancel
          </Button>
          <Button onClick={handleUpdate} disabled={saving} loading={saving}>
            Update Tax
          </Button>
        </Group>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal opened={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Confirm Delete">
        <Text>
          Are you sure you want to delete this tax configuration?
        </Text>
        <Text fw={500} mt="sm">
          Tax Code: {selectedTax?.tax_code}
        </Text>

        <Group justify="flex-end" mt="xl">
          <Button variant="outline" onClick={() => {
            setShowDeleteModal(false);
            window.history.replaceState(null, '', '/dashboard/taxes?mode=list');
          }}>
            Cancel
          </Button>
          <Button color="red" onClick={confirmDelete}>
            Delete
          </Button>
        </Group>
      </Modal>

    </Container>
  );
}

export default TaxesScreen;
